/**
 * Dynamic Menu Hook
 *
 * Fetches CMS menu by slug. All navigation is managed via CMS.
 * Loading states and error handling are exposed for UI components.
 */
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { usePublicMenu } from '@/client/modules/cms/hooks/usePublicCms'
import { useEnabledFeatures } from '@/client/hooks/useFeatures'
import type { MenuItem } from '@/server/modules/cms/db/schema'

// =============================================================================
// Types
// =============================================================================

export interface DynamicMenuItem {
  id: string
  label: string
  href: string
  icon?: string
  description?: string
  external?: boolean
  /** Nested items for grouped menus (categories) */
  children?: DynamicMenuItem[]
  /** Item type: 'page', 'custom', 'category', 'action' */
  type?: string
}

export interface UseDynamicMenuOptions {
  /** Whether user is admin (for filtering admin-only items) */
  isAdmin?: boolean
}

export interface UseDynamicMenuResult {
  /** Menu items in DynamicMenuItem format (preserves hierarchy) */
  items: DynamicMenuItem[]
  /** Flat list of link items (excludes categories, includes children) */
  flatItems: DynamicMenuItem[]
  /** Check if an item is active based on current path */
  isActive: (item: DynamicMenuItem) => boolean
  /** Whether data is loading from CMS */
  isLoading: boolean
  /** Error if menu fetch failed */
  error: Error | null
  /** Retry function to refetch menu */
  refetch: () => void
}

// =============================================================================
// Utilities
// =============================================================================

/**
 * Filter CMS menu items by enabled features
 * Items with featureKey are hidden if that feature is disabled
 */
function filterCmsItemsByFeatures(
  items: MenuItem[],
  enabledFeatures: Set<string>
): MenuItem[] {
  return items
    .filter((item) => {
      // No featureKey = always visible
      if (!item.featureKey) return true
      // Has featureKey = only visible if feature is enabled
      return enabledFeatures.has(item.featureKey)
    })
    .map((item) => ({
      ...item,
      // Recursively filter children
      children: item.children
        ? filterCmsItemsByFeatures(item.children, enabledFeatures)
        : undefined,
    }))
    // Remove categories that have no visible children after filtering
    .filter((item) => {
      if (item.type === 'category' && item.children) {
        return item.children.length > 0
      }
      return true
    })
}

/**
 * Convert CMS MenuItem to DynamicMenuItem format
 */
function convertCmsMenuItem(item: MenuItem): DynamicMenuItem {
  return {
    id: item.id,
    label: item.label || '',
    href: item.target || '',
    icon: item.icon || undefined,
    external: item.newTab || false,
    type: item.type || 'page',
    children: item.children?.map(convertCmsMenuItem),
  }
}

/**
 * Flatten nested menu items (extracts all linkable items)
 */
function flattenDynamicItems(items: DynamicMenuItem[]): DynamicMenuItem[] {
  const flat: DynamicMenuItem[] = []

  for (const item of items) {
    // Include items with hrefs (skip empty category headers)
    if (item.href) {
      flat.push(item)
    }
    // Recurse into children
    if (item.children?.length) {
      flat.push(...flattenDynamicItems(item.children))
    }
  }

  return flat
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Dynamic menu hook that fetches from CMS
 *
 * @param cmsSlug - The CMS menu slug to fetch (e.g., 'dashboard-sidebar')
 * @param options - Optional configuration
 * @returns Menu items, loading state, error, and utility functions
 */
export function useDynamicMenu(
  cmsSlug: string,
  options: UseDynamicMenuOptions = {}
): UseDynamicMenuResult {
  const location = useLocation()
  const { isAdmin = false } = options

  // Fetch CMS menu
  const { data: cmsData, isLoading, error, refetch } = usePublicMenu(cmsSlug)

  // Fetch enabled features (for menu item filtering)
  const { enabledFeatures } = useEnabledFeatures()

  // Convert and filter menu items
  const items = useMemo<DynamicMenuItem[]>(() => {
    if (!cmsData?.menu?.items?.length) {
      return []
    }

    // Filter by enabled features, then convert
    const filteredByFeatures = filterCmsItemsByFeatures(
      cmsData.menu.items,
      enabledFeatures
    )

    // Convert to DynamicMenuItem format
    let converted = filteredByFeatures.map(convertCmsMenuItem)

    // Filter admin-only items if not admin
    if (!isAdmin) {
      converted = converted.filter((item) => {
        // Skip items with /admin in href if not admin
        if (item.href?.includes('/admin')) return false
        return true
      })
    }

    return converted
  }, [cmsData, enabledFeatures, isAdmin])

  // Flatten items for simpler iteration (removes hierarchy)
  const flatItems = useMemo(() => flattenDynamicItems(items), [items])

  // Active state checker based on current path
  const isActive = useMemo(
    () => (item: DynamicMenuItem) => {
      if (!item.href) return false
      const path = location.pathname

      // Exact match
      if (item.href === path) return true

      // Prefix match for nested routes (but not for root paths like '/')
      if (item.href.length > 1 && path.startsWith(item.href + '/')) {
        return true
      }

      return false
    },
    [location.pathname]
  )

  return {
    items,
    flatItems,
    isActive,
    isLoading,
    error: error as Error | null,
    refetch,
  }
}

// =============================================================================
// Specialized Hooks
// =============================================================================

/**
 * Hook for public header navigation
 */
export function useDynamicHeaderMenu() {
  return useDynamicMenu('public-header')
}

/**
 * Hook for public footer navigation
 */
export function useDynamicFooterMenu() {
  return useDynamicMenu('public-footer')
}

/**
 * Hook for dashboard sidebar navigation
 */
export function useDynamicDashboardMenu(options: { isAdmin?: boolean } = {}) {
  return useDynamicMenu('dashboard-sidebar', {
    isAdmin: options.isAdmin,
  })
}

/**
 * Hook for user account dropdown navigation
 */
export function useDynamicUserAccountMenu(options: { isAdmin?: boolean } = {}) {
  return useDynamicMenu('user-account', {
    isAdmin: options.isAdmin,
  })
}
